import { z } from "zod";

/**
 * Permission catalogue. Roles are per-organization records holding a set of
 * these keys; five system roles are seeded for every org and custom roles can
 * be created by owners/admins. The API enforces permissions on every route;
 * the web app uses the same keys to hide/disable UI.
 */
export const PERMISSIONS = {
  // Workspace administration
  "org:manage": "Edit organization profile, branding, tax & invoice settings",
  "members:manage": "Invite, remove members and change roles",
  "roles:manage": "Create and edit custom roles",
  "billing:manage": "Manage the subscription / plan",
  "integrations:manage": "Connect and configure integrations, API keys and webhooks",
  "audit:view": "View the activity log / audit trail",
  "data:export": "Export data (CSV / full workspace export)",
  "data:import": "Import data from CSV / other tools",
  // Sales
  "leads:read": "View leads and the sales pipeline",
  "leads:write": "Create and edit leads, move pipeline stages",
  "leads:delete": "Delete leads",
  "leads:convert": "Convert a won lead into a client",
  "pipeline:manage": "Configure pipelines, stages, sources and lost reasons",
  // Clients
  "clients:read": "View clients and contacts",
  "clients:write": "Create and edit clients and contacts",
  "clients:delete": "Archive / delete clients",
  // Proposals & contracts
  "proposals:read": "View proposals and quotes",
  "proposals:write": "Create, edit and send proposals",
  "contracts:read": "View contracts",
  "contracts:write": "Create, edit and send contracts",
  "contracts:sign": "Countersign contracts on behalf of the agency",
  // Finance
  "financials:view": "See monetary values (rates, budgets, revenue, profitability)",
  "invoices:read": "View invoices, credit notes and payments",
  "invoices:write": "Create and edit draft invoices, credit notes",
  "invoices:send": "Issue / send invoices and reminders",
  "payments:record": "Record, refund and reconcile payments",
  "expenses:read": "View expenses",
  "expenses:write": "Create and edit expenses",
  "expenses:approve": "Approve expenses",
  // Delivery
  "projects:read": "View projects the user can access",
  "projects:read_all": "View all projects, including ones the user is not a member of",
  "projects:write": "Create and edit projects, milestones and tasks",
  "projects:delete": "Archive / delete projects",
  "tasks:write": "Create, edit and complete tasks",
  "time:track": "Track own time",
  "time:manage": "Edit anyone's time entries and approve timesheets",
  "templates:manage": "Manage project, task and onboarding templates",
  // Collaboration
  "files:read": "View files",
  "files:write": "Upload and manage files",
  "portal:manage": "Manage client portal access and what clients can see",
  "messages:write": "Post comments and client messages",
  "calendar:write": "Create meetings and manage booking pages",
  // Intelligence
  "reports:view": "View dashboards and reports",
  "ai:use": "Use the Ask-your-business AI assistant",
  "automations:manage": "Create and edit workflow automations",
  "forms:manage": "Create and edit lead capture forms",
} as const;

export type Permission = keyof typeof PERMISSIONS;
export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as Permission[];
export const permissionSchema = z.enum(PERMISSION_KEYS as [Permission, ...Permission[]]);

export const SYSTEM_ROLE_KEYS = ["owner", "admin", "manager", "finance", "member"] as const;
export type SystemRoleKey = (typeof SYSTEM_ROLE_KEYS)[number];

const ALL = PERMISSION_KEYS;
const without = (...exclude: Permission[]) => ALL.filter((p) => !exclude.includes(p));

export const SYSTEM_ROLES: Record<SystemRoleKey, { name: string; description: string; permissions: readonly Permission[] }> = {
  owner: { name: "Owner", description: "Full access, including billing and ownership transfer", permissions: ALL },
  admin: { name: "Admin", description: "Full access except billing", permissions: without("billing:manage") },
  manager: {
    name: "Manager",
    description: "Runs clients, sales and delivery; sees financials; no workspace admin",
    permissions: without("org:manage", "members:manage", "roles:manage", "billing:manage", "integrations:manage", "data:import", "payments:record"),
  },
  finance: {
    name: "Finance",
    description: "Invoicing, payments, expenses and reports",
    permissions: [
      "clients:read", "clients:write", "leads:read", "proposals:read", "contracts:read",
      "financials:view", "invoices:read", "invoices:write", "invoices:send", "payments:record",
      "expenses:read", "expenses:write", "expenses:approve", "projects:read", "projects:read_all",
      "time:track", "time:manage", "files:read", "files:write", "messages:write", "reports:view",
      "ai:use", "audit:view", "data:export",
    ],
  },
  member: {
    name: "Team member",
    description: "Works on assigned projects and tasks, tracks time; no financials",
    permissions: [
      "clients:read", "leads:read", "projects:read", "tasks:write", "time:track", "files:read",
      "files:write", "messages:write", "calendar:write", "expenses:read", "expenses:write", "ai:use",
    ],
  },
};

export function hasPermission(granted: readonly string[] | ReadonlySet<string>, needed: Permission): boolean {
  return granted instanceof Set ? granted.has(needed) : (granted as readonly string[]).includes(needed);
}
